const DEFAULT_LOCAL = "http://localhost:4010";

/**
 * Browser API base URL.
 * Production uses same-origin "" so the web BFF proxies /v1 (first-party cookies).
 * Set VITE_API_BASE_URL only for intentional cross-origin diagnostics — not for auth.
 */
export function getApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL?.trim();
  if (fromEnv === "" || fromEnv === "__SAME_ORIGIN__") return "";
  // Production default: same-origin BFF (ignore legacy absolute VITE_API_BASE_URL).
  if (import.meta.env.PROD) {
    if (fromEnv && fromEnv.startsWith("http") && import.meta.env.VITE_ALLOW_CROSS_ORIGIN_API === "true") {
      return fromEnv.replace(/\/$/, "");
    }
    return "";
  }
  if (fromEnv) return fromEnv.replace(/\/$/, "");
  return DEFAULT_LOCAL;
}

export type HealthResponse = {
  status: string;
  service?: string;
  mode?: string;
  liveExecutionEnabled?: boolean;
  deploymentPerformed?: boolean;
};

export type ReadinessResponse = {
  status: string;
  storageMode?: string;
  uptimeSeconds?: number;
};

export type ApiProbeResult<T> = {
  ok: boolean;
  statusCode: number | null;
  data: T | null;
  error: string | null;
  fetchedAt: string;
};

async function getJson<T>(path: string): Promise<ApiProbeResult<T>> {
  const fetchedAt = new Date().toISOString();
  const url = `${getApiBaseUrl()}${path}`;
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      // Explicitly no credentials for public probes
      credentials: "omit",
      cache: "no-store",
    });
    if (!res.ok) {
      return {
        ok: false,
        statusCode: res.status,
        data: null,
        error: `HTTP ${res.status}`,
        fetchedAt,
      };
    }
    const data = (await res.json()) as T;
    return { ok: true, statusCode: res.status, data, error: null, fetchedAt };
  } catch (err) {
    return {
      ok: false,
      statusCode: null,
      data: null,
      error: err instanceof Error ? err.message : "Network error",
      fetchedAt,
    };
  }
}

/** Safe public endpoints only — never send tokens or call protected APIs. */
export function fetchHealth() {
  return getJson<HealthResponse>("/health");
}

export function fetchReadiness() {
  return getJson<ReadinessResponse>("/readiness");
}
